/**
 * Call follow-ups: the work a call leaves for a person (plan:
 * docs/plans/call-follow-ups.md).
 *
 * ONE VOCABULARY FOR EVERY PRODUCER. The voice worker reports facts, the API
 * files fees and lost calls, the evaluator files reviews, campaigns hand runs
 * back. Every one of them is decided by `decideCallFollowUps` and written by
 * the tasks domain's one follow-up service, and this module is what they
 * agree on: the kinds, what each is worth, how a duplicate is recognised.
 *
 * PRIORITY AND LABELS ARE THE POLICY'S, NEVER THE PRODUCER'S. A producer says
 * what happened; this table says how much it matters.
 *
 * STAT IS `urgent` PLUS THE `stat` LABEL. Task priority has no `stat` value;
 * the inbox sorts the `stat` label first (`urgencyBucket` in the tasks
 * repository), so a STAT is expressed with what exists.
 *
 * WHERE THE WORK GOES depends on the call. An inbound call has no task of its
 * own, so its follow-ups are new tasks. An outbound call was placed for a task
 * (the referral, usually): work that continues that task is a note on it, and
 * different work is a sub-task under it. The table below says which, per kind.
 */

// =============================================================================
// Kinds
// =============================================================================

export const CALL_FOLLOW_UP_KIND = {
  /** The caller asked to be rung back. */
  CALLBACK: 'callback',
  /** A message was taken, or a handoff promised a call back. */
  MESSAGE: 'message',
  /** Things the caller raised that the call did not resolve. */
  SIDE_ASKS: 'side_asks',
  /** A verified caller wanted an appointment and left without one. */
  NO_APPOINTMENT: 'no_appointment',
  /** The EHR refused a booking made on the call. */
  EHR_REFUSED: 'ehr_refused',
  /** An appointment was cancelled late on the call and a fee applies. */
  CANCELLATION_FEE: 'cancellation_fee',
  /** The assistant failed during the call, as the worker reported it. */
  CALL_FAILED: 'call_failed',
  /** The call never reported its end: the worker died (reaper, watchdog). */
  WORKER_LOST: 'worker_lost',
  /** The post-call evaluator marked the call red (`needs_you`). */
  EVAL_REVIEW: 'eval_review',
  /** The post-call evaluator marked the call yellow (`follow_up`). */
  EVAL_FOLLOW_UP: 'eval_follow_up',
  /** The post-call evaluator asked for a callback (outbound, `CALL_EVAL_CREATE_CALLBACKS`). */
  EVAL_CALLBACK: 'eval_callback',
  /** The call's follow-up facts could not all be read. */
  CALL_REVIEW: 'call_review',
  /** A campaign run ended in a way staff should hear about (not a hand-back). */
  CAMPAIGN_OUTCOME: 'campaign_outcome',
  /** A campaign run handed the referral back to a person (ADR-318, P9). */
  NEEDS_PERSON: 'needs_person',
} as const;

export type CallFollowUpKind = (typeof CALL_FOLLOW_UP_KIND)[keyof typeof CALL_FOLLOW_UP_KIND];

export const CALL_FOLLOW_UP_KINDS = Object.values(CALL_FOLLOW_UP_KIND) as [
  CallFollowUpKind,
  ...CallFollowUpKind[],
];

/** How a kind reads in the UI. */
export const CALL_FOLLOW_UP_KIND_LABELS: Readonly<Record<CallFollowUpKind, string>> = {
  callback: 'Callback',
  message: 'Message',
  side_asks: 'Caller asks',
  no_appointment: 'No appointment',
  ehr_refused: 'Booking refused by the EHR',
  cancellation_fee: 'Cancellation fee',
  call_failed: 'Call failed',
  worker_lost: 'Call lost',
  eval_review: 'Call needs review',
  eval_follow_up: 'Call needs follow-up',
  eval_callback: 'Callback',
  call_review: 'Call needs review',
  campaign_outcome: 'Campaign outcome',
  needs_person: 'Needs a person',
};

// =============================================================================
// Labels
// =============================================================================

/**
 * Classification label names a call follow-up can carry. All but `stat` and
 * the needs-person label are seeded per org as procedural system labels with
 * no classification prompt (classification-labels/seed.ts), so the email
 * classifier never assigns them.
 */
export const CALL_FOLLOW_UP_LABEL = {
  CALLBACK: 'callback',
  MESSAGE: 'message',
  CALLER_ASKS: 'caller_asks',
  NO_APPOINTMENT: 'no_appointment',
  CANCELLATION_FEE: 'cancellation_fee',
  CALL_FAILED: 'call_failed',
  CALL_REVIEW: 'call_review',
  CALL_FOLLOW_UP: 'call_follow_up',
  /** The existing system label that sorts first in the inbox. */
  STAT: 'stat',
} as const;

/** The labels the call follow-up seed adds (everything except the existing `stat`). */
export const SEEDED_CALL_FOLLOW_UP_LABELS = Object.values(CALL_FOLLOW_UP_LABEL).filter(
  (label) => label !== CALL_FOLLOW_UP_LABEL.STAT
);

/** The label a campaign hand-back puts on the referral task (ADR-318, P9). */
export const OUTREACH_NEEDS_PERSON_LABEL = 'outreach:needs-person';

/**
 * Labels that belong to work in progress rather than to the document a task
 * came from. A label recompute on an email-born task (an attachment's labels
 * edited, an email replayed) rebuilds `tasks.labels` from the attachments and
 * would otherwise drop these (`keepWorkflowLabels`, apps/api/src/lib).
 */
export const WORKFLOW_LABELS: readonly string[] = [
  ...Object.values(CALL_FOLLOW_UP_LABEL),
  OUTREACH_NEEDS_PERSON_LABEL,
];

// =============================================================================
// Priority
// =============================================================================

/** Task priority, restated: `packages/shared` cannot import the tasks schema. */
export type FollowUpPriority = 'low' | 'medium' | 'high' | 'urgent';

const PRIORITY_RANK: Readonly<Record<FollowUpPriority, number>> = {
  low: 0,
  medium: 1,
  high: 2,
  urgent: 3,
};

/** Whether `candidate` outranks `current`. An unknown current value never outranks. */
export function outranks(candidate: FollowUpPriority, current: string | null | undefined): boolean {
  const rank = current ? PRIORITY_RANK[current as FollowUpPriority] : undefined;
  return rank === undefined || PRIORITY_RANK[candidate] > rank;
}

// =============================================================================
// Policy
// =============================================================================

/**
 * What an outbound call's follow-up does to the task the call was placed for.
 *
 *   note   it continues that task's work: an audited note on it, raising its
 *          priority to `raiseTo` (never lowering it) and adding `labels`
 *   sub    it is different work: a sub-task under it
 *   none   the call's task already IS this work; nothing more is filed
 */
export type OnCallTask =
  | { place: 'note'; raiseTo: FollowUpPriority | null; labels: readonly string[] }
  | { place: 'sub' }
  | { place: 'none' };

/**
 * How a duplicate is recognised for a NEW task (or sub-task).
 *
 *   interaction   one per kind per call
 *   stat          one STAT task per call, whichever kind files it first: a
 *                 failed call the evaluator then marks red is one STAT task,
 *                 and the second producer notes on it instead
 *   run           one per campaign run (hand-backs and campaign outcomes)
 */
export type FollowUpDedupe = 'interaction' | 'stat' | 'run';

export interface CallFollowUpPolicy {
  /** A new task: an inbound call, or an outbound call with no task of its own. */
  task: {
    priority: Readonly<Record<'inbound' | 'outbound', FollowUpPriority>>;
    labels: readonly string[];
  } | null;
  /** An outbound call that has a task of its own. */
  onCallTask: OnCallTask;
  dedupe: FollowUpDedupe;
}

const STAT_NOTE: OnCallTask = {
  place: 'note',
  raiseTo: 'urgent',
  labels: [CALL_FOLLOW_UP_LABEL.STAT],
};
const HIGH_NOTE: OnCallTask = { place: 'note', raiseTo: 'high', labels: [] };

function both(
  priority: FollowUpPriority
): Readonly<Record<'inbound' | 'outbound', FollowUpPriority>> {
  return { inbound: priority, outbound: priority };
}

/**
 * What each kind is worth, and where it goes (plan §4). Inbound priorities are
 * the front office's (Aryan, 2026-10-21); outbound keeps what each kind had
 * before inbound follow-ups existed.
 */
export const CALL_FOLLOW_UP_POLICY: Readonly<Record<CallFollowUpKind, CallFollowUpPolicy>> = {
  callback: {
    task: {
      priority: { inbound: 'urgent', outbound: 'high' },
      labels: [CALL_FOLLOW_UP_LABEL.CALLBACK],
    },
    // Every outbound callback is a callback sub-task, campaign or drawer: it
    // is work with a time on it that somebody completes (Aryan, 2026-10-21).
    onCallTask: { place: 'sub' },
    dedupe: 'interaction',
  },
  message: {
    task: { priority: both('high'), labels: [CALL_FOLLOW_UP_LABEL.MESSAGE] },
    // A message taken for someone is different work. A handoff's promise of a
    // call back continues the call's own task: `decideCallFollowUps` notes it.
    onCallTask: { place: 'sub' },
    dedupe: 'interaction',
  },
  side_asks: {
    task: {
      priority: { inbound: 'high', outbound: 'medium' },
      labels: [CALL_FOLLOW_UP_LABEL.CALLER_ASKS],
    },
    onCallTask: { place: 'sub' },
    dedupe: 'interaction',
  },
  no_appointment: {
    task: { priority: both('high'), labels: [CALL_FOLLOW_UP_LABEL.NO_APPOINTMENT] },
    onCallTask: { place: 'none' },
    dedupe: 'interaction',
  },
  ehr_refused: {
    // Today's EHR follow-up task carries no labels; it keeps its own title.
    task: { priority: both('high'), labels: [] },
    onCallTask: HIGH_NOTE,
    dedupe: 'interaction',
  },
  cancellation_fee: {
    task: { priority: both('high'), labels: [CALL_FOLLOW_UP_LABEL.CANCELLATION_FEE] },
    onCallTask: { place: 'sub' },
    dedupe: 'interaction',
  },
  call_failed: {
    task: {
      priority: both('urgent'),
      labels: [CALL_FOLLOW_UP_LABEL.CALL_FAILED, CALL_FOLLOW_UP_LABEL.STAT],
    },
    onCallTask: STAT_NOTE,
    dedupe: 'stat',
  },
  worker_lost: {
    task: {
      priority: both('urgent'),
      labels: [CALL_FOLLOW_UP_LABEL.CALL_FAILED, CALL_FOLLOW_UP_LABEL.STAT],
    },
    onCallTask: STAT_NOTE,
    dedupe: 'stat',
  },
  eval_review: {
    task: {
      priority: both('urgent'),
      labels: [CALL_FOLLOW_UP_LABEL.CALL_REVIEW, CALL_FOLLOW_UP_LABEL.STAT],
    },
    onCallTask: STAT_NOTE,
    dedupe: 'stat',
  },
  eval_follow_up: {
    task: { priority: both('high'), labels: [CALL_FOLLOW_UP_LABEL.CALL_FOLLOW_UP] },
    onCallTask: HIGH_NOTE,
    dedupe: 'interaction',
  },
  eval_callback: {
    task: { priority: both('high'), labels: [CALL_FOLLOW_UP_LABEL.CALLBACK] },
    onCallTask: HIGH_NOTE,
    dedupe: 'interaction',
  },
  call_review: {
    task: { priority: both('high'), labels: [CALL_FOLLOW_UP_LABEL.CALL_REVIEW] },
    onCallTask: HIGH_NOTE,
    dedupe: 'interaction',
  },
  campaign_outcome: {
    // Only ever a note on the referral; the raise depends on the outcome.
    task: null,
    onCallTask: { place: 'note', raiseTo: null, labels: [] },
    dedupe: 'run',
  },
  needs_person: {
    task: null,
    onCallTask: { place: 'note', raiseTo: 'high', labels: [OUTREACH_NEEDS_PERSON_LABEL] },
    dedupe: 'run',
  },
};

// =============================================================================
// Keys
// =============================================================================

/**
 * The key that makes filing idempotent: unique per org on `tasks.follow_up_key`.
 *
 * THROWS ON A MISSING PART. A key built from `undefined` would collapse every
 * such task in an org into one.
 */
export function callFollowUpKey(
  kind: CallFollowUpKind,
  ids: { interactionId?: string | null; runId?: string | null }
): string {
  const { dedupe } = CALL_FOLLOW_UP_POLICY[kind];
  if (dedupe === 'run') {
    if (!ids.runId) throw new Error(`a ${kind} follow-up needs a runId`);
    return `${kind}:${ids.runId}`;
  }
  if (!ids.interactionId) throw new Error(`a ${kind} follow-up needs an interactionId`);
  return dedupe === 'stat' ? `stat:${ids.interactionId}` : `${kind}:${ids.interactionId}`;
}

/** The key of a note: one per trigger, per kind, per subject (a call or a run). */
export function callFollowUpNoteKey(
  trigger: string,
  kind: CallFollowUpKind,
  subjectId: string
): string {
  if (!subjectId) throw new Error(`a ${kind} note needs a subject id`);
  return `note:${trigger}:${kind}:${subjectId}`;
}

/**
 * The key of the sub-task a note becomes when the task it was meant for is
 * closed. One level only: the fallback of a fallback is never built.
 */
export function closedTaskFallbackKey(key: string, closedTaskId: string): string {
  if (key.includes(':after:')) throw new Error('a closed-task fallback is never itself re-homed');
  return `${key}:after:${closedTaskId}`;
}
